import React from 'react';
import logoImg from '../assets/images/Logo.webp';

export const LOGO_ASSET = logoImg;

/**
 * App Icon Component that renders the official brand logo from src/assets/images/Logo.png
 * Scalable from 32px up to 512px with crisp aspect ratio and high resolution.
 */
export const AppIconSVG: React.FC<{
  size?: number | string;
  className?: string;
  theme?: 'light' | 'dark';
}> = ({ size = 42, className = '', theme = 'light' }) => {
  const pixelSize = typeof size === 'number' ? `${size}px` : size;

  return (
    <div
      style={{ width: pixelSize, height: pixelSize }}
      className={`relative inline-flex items-center justify-center flex-shrink-0 select-none transition-transform duration-300 ${className}`}
    >
      <img
        src={logoImg}
        alt="Uniform Loop Logo"
        className="w-full h-full object-contain object-center block select-none"
        style={{ width: pixelSize, height: pixelSize }}
        loading="eager"
        decoding="async"
      />
    </div>
  );
};

export interface LogoProps {
  className?: string;
  variant?: 'full' | 'horizontal' | 'icon' | 'compact' | 'small';
  theme?: 'light' | 'dark';
  size?: 'sm' | 'md' | 'lg' | 'xl';
  showTagline?: boolean;
}

/**
 * Responsive Branding System Component for Uniform Loop Immingham
 * Supports 5 Variants:
 * 1. 'full': Primary Brand Logo (Icon top, clean Wordmark, IMMINGHAM, Tagline)
 * 2. 'horizontal': Website Header Logo (Horizontal [Icon] + Wordmark & IMMINGHAM, optimized ~320x70)
 * 3. 'icon': Pure Icon Only (For Favicon, App Icon, Login screens, 32px-512px)
 * 4. 'compact': Compact Horizontal Logo ([Icon] Uniform Loop)
 * 5. 'small': Small Navigation Logo (Optimized for tight 40-50px headers)
 */
export const Logo: React.FC<LogoProps> = ({
  className = '',
  variant = 'horizontal',
  theme = 'light',
  size = 'md',
  showTagline = false
}) => {
  // Color configuration based on Light vs Dark theme
  const navyTextColor = theme === 'dark' ? 'text-white' : 'text-[#001B48]';
  const blueTextColor = theme === 'dark' ? 'text-blue-400' : 'text-[#2563EB]';
  const subTextColor = theme === 'dark' ? 'text-slate-300' : 'text-slate-500';

  // Sizing matrix for Icon & Typography
  const sizeConfig = {
    sm: {
      iconSize: 36,
      fullIconSize: 84,
      wordmark: 'text-lg',
      subtext: 'text-[9px]',
      gap: 'gap-2'
    },
    md: {
      iconSize: 46,
      fullIconSize: 124,
      wordmark: 'text-xl sm:text-2xl',
      subtext: 'text-[10px] sm:text-[11px]',
      gap: 'gap-3'
    },
    lg: {
      iconSize: 58,
      fullIconSize: 156,
      wordmark: 'text-2xl sm:text-3xl',
      subtext: 'text-[11px] sm:text-xs',
      gap: 'gap-3.5'
    },
    xl: {
      iconSize: 76,
      fullIconSize: 196,
      wordmark: 'text-3xl sm:text-4xl',
      subtext: 'text-xs sm:text-sm',
      gap: 'gap-4'
    }
  };

  const current = sizeConfig[size];

  // 3. ICON ONLY VARIANT
  if (variant === 'icon') {
    return (
      <div className={`inline-flex items-center justify-center ${className}`}>
        <AppIconSVG size={current.iconSize} theme={theme} />
      </div>
    );
  }

  // 4. COMPACT LOGO VARIANT ([Icon] Uniform Loop)
  if (variant === 'compact') {
    return (
      <div className={`inline-flex items-center ${current.gap} group cursor-pointer ${className}`}>
        <AppIconSVG size={current.iconSize} theme={theme} />
        <div className={`font-heading font-extrabold tracking-tight whitespace-nowrap ${current.wordmark}`}>
          <span className={navyTextColor}>Uniform</span>{' '}
          <span className={blueTextColor}>Loop</span>
        </div>
      </div>
    );
  }

  // 5. SMALL NAVIGATION LOGO VARIANT (Ultra clean 40-50px)
  if (variant === 'small') {
    return (
      <div className={`inline-flex items-center gap-2 group cursor-pointer ${className}`}>
        <AppIconSVG size={36} theme={theme} />
        <div className="flex flex-col justify-center leading-tight">
          <div className="font-heading font-extrabold text-lg tracking-tight whitespace-nowrap">
            <span className={navyTextColor}>Uniform</span>{' '}
            <span className={blueTextColor}>Loop</span>
          </div>
          <span className={`text-[9px] font-bold tracking-[0.2em] uppercase ${subTextColor}`}>
            IMMINGHAM
          </span>
        </div>
      </div>
    );
  }

  // 1. PRIMARY BRAND LOGO VARIANT (Full Stacked Version for Splash / Posters / Login / Footers)
  if (variant === 'full') {
    return (
      <div className={`flex flex-col items-center text-center group cursor-pointer ${className}`}>
        <div className="p-2.5 sm:p-3 rounded-3xl bg-white shadow-md border border-slate-100 hover:shadow-xl hover:scale-105 transition-all duration-300 flex items-center justify-center">
          <AppIconSVG size={current.fullIconSize} theme={theme} />
        </div>
        <div className="mt-4 flex flex-col items-center">
          <div className={`font-heading font-black tracking-tight ${current.wordmark}`}>
            <span className={navyTextColor}>Uniform</span>{' '}
            <span className={blueTextColor}>Loop</span>
          </div>
          <span className={`font-bold tracking-[0.25em] uppercase mt-1.5 ${current.subtext} ${subTextColor}`}>
            IMMINGHAM
          </span>
          {showTagline && (
            <p className="mt-3 text-xs font-semibold text-blue-900 bg-blue-50/90 px-4 py-1.5 rounded-full border border-blue-100 tracking-wide">
              Giving School Uniform a Second Life
            </p>
          )}
        </div>
      </div>
    );
  }

  // 2. WEBSITE HEADER LOGO (Most Important Horizontal Variant)
  // Clean wordmark: Navy "Uniform", Blue "Loop", Grey "IMMINGHAM".
  return (
    <div className={`inline-flex items-center ${current.gap} group cursor-pointer ${className}`}>
      {/* Icon */}
      <div className="flex-shrink-0 flex items-center justify-center group-hover:scale-105 transition-transform duration-300">
        <AppIconSVG size={current.iconSize} theme={theme} />
      </div>

      {/* Wordmark & Location */}
      <div className="flex flex-col justify-center leading-tight">
        <div className={`font-heading font-black tracking-tight whitespace-nowrap ${current.wordmark}`}>
          <span className={navyTextColor}>Uniform</span>{' '}
          <span className={blueTextColor}>Loop</span>
        </div>
        <span className={`font-bold tracking-[0.2em] uppercase mt-0.5 ${current.subtext} ${subTextColor}`}>
          IMMINGHAM
        </span>
      </div>
    </div>
  );
};
